import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, TextField, RadioGroup, FormControlLabel, Radio,
  Alert
} from '@mui/material';
import { ExitToApp } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Enrollment } from '../../types/sessions';
import { enrollmentsApi } from '../../services/sessions/enrollmentsApi';

interface ExitEnrollmentDialogProps {
  open: boolean;
  enrollment: Enrollment | null;
  studentName?: string;
  onClose: () => void;
  onExited: () => void;
}

const ExitEnrollmentDialog: React.FC<ExitEnrollmentDialogProps> = ({
  open, enrollment, studentName, onClose, onExited
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<'transferred_out' | 'dropped_out'>('transferred_out');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStatus('transferred_out');
    setReason('');
  }, [open]);

  const submit = async () => {
    if (!enrollment) return;
    if (!reason.trim()) {
      enqueueSnackbar('Please add a brief reason', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await enrollmentsApi.exit(enrollment._id, { status, reason: reason.trim() });
      enqueueSnackbar('Enrollment closed', { variant: 'success' });
      onExited();
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not close enrollment', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!enrollment) return null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <ExitToApp color="warning" fontSize="small" />
          <Typography variant="h6" fontWeight={600}>Close enrollment</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Marks <strong>{studentName ?? 'this student'}</strong>’s enrollment as terminated in this session.
          The student record remains; they can be enrolled in another session later.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          <RadioGroup value={status} onChange={(_, v) => setStatus(v as any)}>
            <FormControlLabel
              value="transferred_out"
              control={<Radio />}
              label={
                <span>
                  <strong>Transferred out</strong>
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                    Left to attend another school.
                  </Typography>
                </span>
              }
            />
            <FormControlLabel
              value="dropped_out"
              control={<Radio />}
              label={
                <span>
                  <strong>Dropped out</strong>
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                    Left without a destination.
                  </Typography>
                </span>
              }
            />
          </RadioGroup>

          <TextField
            label="Reason"
            placeholder={status === 'transferred_out'
              ? 'e.g. Family relocated to Lahore'
              : 'e.g. Financial reasons'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            required
          />

          <Alert severity="info" variant="outlined">
            You can re-enroll this student in a future session at any time from their profile.
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          color="warning"
          variant="contained"
          disableElevation
          disabled={submitting || !reason.trim()}
          onClick={submit}
        >
          {submitting ? 'Closing…' : 'Close enrollment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExitEnrollmentDialog;
